import React, { useEffect, useState } from 'react';
import { Container, Row, Col, Card, Form, Spinner } from 'react-bootstrap';
import { PieChart, Pie, Cell, Tooltip, ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Legend, CartesianGrid } from 'recharts';
import axios from '../api/axios';

const CouponsKPI = ({ outlets, loadingOutlets }) => {
    const token = localStorage.getItem('authToken');

    const [timeFilter, setTimeFilter] = useState('weekly');
    const [couponCategoryFilter, setCouponCategoryFilter] = useState('both');
    const [outletFilter, setOutletFilter] = useState('all');
    const [foodBeverageUsage, setFoodBeverageUsage] = useState();
    const [couponUsage, setCouponUsage] = useState({
        mostUsedCoupons: [],
        leastUsedCoupons: [],
    });
    const [loadingData, setLoadingData] = useState(true);

    const fetchCouponUsage = async () => {
        try {
            setLoadingData(true);
            const headers = { Authorization: `Bearer ${token}` };

            // let url = `dashboard/getCouponUsageReport`;
            let url = `dashboard/getCouponUsageReportThroughSummary`
            let queryParams = [];

            if (outletFilter !== 'all') {
                queryParams.push(`outletId=${outletFilter}`);
            }
            if (timeFilter === 'weekly') {
                queryParams.push("weekly=true");
            } else if (timeFilter === 'monthly') {
                queryParams.push("monthly=true");
            }
            if (queryParams.length > 0) {
                url += `?${queryParams.join('&')}`;
            }

            const response = await axios.get(url, { headers });
            console.log("Response :", response);
            
            if (response?.data) {
                const { mostUsedCoupons, leastUsedCoupons } = response?.data?.data?.data;

                setCouponUsage({
                    mostUsedCoupons: mostUsedCoupons || [],
                    leastUsedCoupons: leastUsedCoupons || [],
                });
                setFoodBeverageUsage(response?.data?.data?.data);
            }
        } catch (error) {
            console.error('Error fetching coupon usage data:', error);
        } finally {
            setLoadingData(false);
        }
    };

    useEffect(() => {
        fetchCouponUsage();
    }, [timeFilter, outletFilter]);

    const sortedMostUsed = [...couponUsage.mostUsedCoupons].sort(
        (a, b) => b.usageCount - a.usageCount
    );
    const sortedLeastUsed = [...couponUsage.leastUsedCoupons].sort(
        (a, b) => a.usageCount - b.usageCount
    );

    const getBarChartData = () => {
        let data = [];
        if (couponCategoryFilter === 'most' || couponCategoryFilter === 'both') {
            data = data.concat(
                sortedMostUsed.map((item) => ({
                    ...item,
                    type: 'Most Used',
                }))
            );
        }
        if (couponCategoryFilter === 'least' || couponCategoryFilter === 'both') {
            data = data.concat(
                sortedLeastUsed.map((item) => ({
                    ...item,
                    type: 'Least Used',
                }))
            );
        }
        return data;
    };
    const barChartData = getBarChartData();

    // Prepare Pie Chart data
    const pieData = [
        { name: 'Food', value: foodBeverageUsage?.foodCount },
        { name: 'Beverage', value: foodBeverageUsage?.beverageCount },
    ];
    const pieColors = ['#0088FE', '#FF8042'];

    return (
        <Container fluid className="py-4 shadow-sm" style={{ backgroundColor: 'white' }}>
            {/* Filters Row */}
            <h4 className='fw-bold'>COUPON ANALYTICS</h4>
            <hr />
            <Row className="mb-4 g-2">
                <Col md={4}>
                    <Form.Group>
                        <Form.Label>Time Filter</Form.Label>
                        <Form.Select
                            value={timeFilter}
                            onChange={(e) => setTimeFilter(e.target.value)}
                        >
                            <option value="weekly">Weekly</option>
                            <option value="monthly">Monthly</option>
                        </Form.Select>
                    </Form.Group>
                </Col>

                <Col md={4}>
                    <Form.Group>
                        <Form.Label>Outlet Filter</Form.Label>
                        <Form.Select
                            value={outletFilter}
                            onChange={(e) => setOutletFilter(e.target.value)}
                        >
                            <option value="all">All Outlets</option>
                            {loadingOutlets ? (
                                <option>Loading...</option>
                            ) : (
                                outlets
                                    .filter((outlet) => outlet.status === "ACTIVE")
                                    .map((outlet) => (
                                        <option key={outlet._id} value={outlet._id}>
                                            {outlet.name}
                                        </option>
                                    ))
                            )}
                        </Form.Select>
                    </Form.Group>
                </Col>

                <Col md={4}>
                    <Form.Group>
                        <Form.Label>Coupon Category</Form.Label>
                        <Form.Select
                            value={couponCategoryFilter}
                            onChange={(e) => setCouponCategoryFilter(e.target.value)}
                        >
                            <option value="both">Most + Least</option>
                            <option value="most">Most Used Only</option>
                            <option value="least">Least Used Only</option>
                        </Form.Select>
                    </Form.Group>
                </Col>
            </Row>

            {loadingData ? (
                <Row className="justify-content-center my-5">
                    <Col md="auto">
                        <div className="d-flex flex-column align-items-center">
                            <Spinner animation="border" variant="primary" />
                            <span className="mt-2">Loading coupon data...</span>
                        </div>
                    </Col>
                </Row>
            ) : (
                <Row>
                    <Col md={12} className="mb-4">
                        <Card className="shadow-sm h-100">
                            <Card.Body>
                                <Card.Title>Coupons Usage</Card.Title>
                                {barChartData?.length > 0 ? (
                                    <ResponsiveContainer width="100%" height={400}>
                                        <BarChart data={barChartData} margin={{ top: 20, right: 20, left: 0, bottom: 20 }}>
                                            <CartesianGrid strokeDasharray="3 3" />
                                            <XAxis angle={-55} textAnchor="end" dataKey="couponCode" height={70} />
                                            <YAxis />
                                            <Tooltip />
                                            <Bar dataKey="usageCount" name="Usage Count">
                                                {barChartData.map((entry, index) => {
                                                    const fillColor =
                                                        entry.type === 'Most Used' ? '#82ca9d' : '#ff7875';
                                                    return <Cell key={`cell-${index}`} fill={fillColor} />;
                                                })}
                                            </Bar>
                                        </BarChart>
                                    </ResponsiveContainer>
                                ) : (
                                    <div className="d-flex justify-content-center align-items-center" style={{ height: '400px' }}>
                                        <div className="text-muted">No coupon usage data available</div>
                                    </div>
                                )}
                            </Card.Body>
                        </Card>
                    </Col>

                    {/* Pie Chart: Food vs Beverage */}
                    {/* <Col md={5} className="mb-4">
                        <Card className="shadow-sm h-100">
                            <Card.Body>
                                <Card.Title>Food vs Beverage (Coupons)</Card.Title>
                                {pieData.some(item => item.value > 0) ? (
                                    <>
                                        <ResponsiveContainer width="100%" height={300}>
                                            <PieChart>
                                                <Pie
                                                    data={pieData}
                                                    cx="50%"
                                                    cy="50%"
                                                    outerRadius={90}
                                                    label
                                                    dataKey="value"
                                                >
                                                    {pieData.map((entry, index) => (
                                                        <Cell
                                                            key={`cell-${index}`}
                                                            fill={pieColors[index % pieColors.length]}
                                                        />
                                                    ))}
                                                </Pie>
                                                <Tooltip />
                                            </PieChart>
                                        </ResponsiveContainer>
                                        <div className="d-flex justify-content-center mt-3">
                                            {pieData.map((entry, index) => (
                                                <div
                                                    key={index}
                                                    className="mx-3 d-flex align-items-center"
                                                >
                                                    <div
                                                        style={{
                                                            width: '16px',
                                                            height: '16px',
                                                            backgroundColor: pieColors[index % pieColors.length],
                                                            display: 'inline-block',
                                                            marginRight: '8px',
                                                            borderRadius: '4px',
                                                        }}
                                                    />
                                                    <span>{entry.name}:</span>
                                                    <strong className="ms-1">{entry.value}</strong>
                                                </div>
                                            ))}
                                        </div>
                                    </>
                                ) : (
                                    <div className="d-flex justify-content-center align-items-center" style={{ height: '300px' }}>
                                        <div className="text-muted">No food/beverage data available</div>
                                    </div>
                                )}
                            </Card.Body>
                        </Card>
                    </Col> */}
                </Row>
            )}
        </Container>
    );
};

export default CouponsKPI;